import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type HotelSettingsUsersController from 'lumen/controllers/hotel/settings/users';
import type AggregatedUser from 'lumen/models/aggregated-user';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export interface SubscriptionUserMeta {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface SubscriptionUserData {
  meta: SubscriptionUserMeta;
  users: SubscriptionUser[];
}

export interface AggregatedUserData {
  meta: SubscriptionUserMeta;
  users: AggregatedUser[];
}

export interface RouteParams {
  page?: number;
  product?: string;
  property?: string;
  role?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
}

export interface UsersModel {
  loadAggregatedUsersTask: typeof HotelSettingsUsersRoute.prototype.loadAggregatedUsersTask;
  loadUsersTask: typeof HotelSettingsUsersRoute.prototype.loadUsersTask;
}

export default class HotelSettingsUsersRoute extends Route {
  queryParams = {
    search: { refreshModel: true },
    role: { refreshModel: true },
    product: { refreshModel: true },
    sortBy: { refreshModel: true },
    sortDirection: { refreshModel: true },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  loadAggregatedUsersTask = task(
    { restartable: true },
    async (params: RouteParams) => {
      try {
        const response = await this.store.query<AggregatedUser>(
          'aggregated-user',
          {
            namespace: APIGATEWAY,
            typeKey: 'users/aggregated',
            sort_by: params.sortBy ?? 'name',
            sort_direction: params.sortDirection ?? 'asc',
            page: params.page ?? 1,
            ...(params.search ? { q: params.search } : {}),
            ...(params.role ? { role: params.role } : {}),
            ...(params.product ? { product: params.product } : {}),
          },
        );

        return {
          users: [...response],
          meta: response.meta as unknown as SubscriptionUserMeta,
        } satisfies AggregatedUserData;
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );
  loadUsersTask = task({ restartable: true }, async (params: RouteParams) => {
    try {
      const subscriptionId = this.hotelAndMarketService.hotel.id;

      const response = await this.store.query<SubscriptionUser>(
        'subscription-user',
        {
          namespace: APIGATEWAY,
          typeKey: `subscriptions/${subscriptionId}/users`,
          sort: `${params.sortDirection === 'desc' ? '-' : ''}${params.sortBy ?? 'name'}`,
          page: params.page ?? 1,
          ...(params.search ? { 'filter[q]': params.search } : {}),
          ...(params.role ? { 'filter[role]': params.role } : {}),
          ...(params.product ? { 'filter[product]': params.product } : {}),
        },
      );

      return {
        users: [...response],
        meta: response.meta as unknown as SubscriptionUserMeta,
      } satisfies SubscriptionUserData;
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }
  });

  beforeModel(transition: Transition): void {
    if (!this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.USER_ACCESS)) {
      this.transition.redirectToTab(transition, 'hotel.settings.index');
    }
  }

  model(): UsersModel {
    return {
      loadAggregatedUsersTask: this.loadAggregatedUsersTask,
      loadUsersTask: this.loadUsersTask,
    };
  }

  setupController(
    controller: HotelSettingsUsersController,
    model: UsersModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    void controller.resetAndLoad();
  }
}
